import type {
  NavSectionProps,
  NavListProps,
} from '@/components/nav-section/type';

export const hasPermission = (
  requiredPermissions: string[] = [],
  availablePermissions: string[] = [],
  isSuperAdmin = false
): boolean => {
  if (isSuperAdmin) {
    return true;
  }

  if (!requiredPermissions.length) {
    return true;
  }

  return availablePermissions.some((permission) =>
    requiredPermissions.includes(permission)
  );
};

const filterNavItem = (
  item: NavListProps,
  availablePermissions: string[],
  isSuperAdmin: boolean
): NavListProps | null => {
  if (!hasPermission(item.restricted_to, availablePermissions, isSuperAdmin)) {
    return null;
  }

  if (!item.children?.length) {
    return item;
  }

  const children = item.children
    .map((child: NavListProps) =>
      filterNavItem(child, availablePermissions, isSuperAdmin)
    )
    .filter(Boolean) as NavListProps[];

  if (!children.length) {
    return null;
  }

  return {
    ...item,
    children,
  };
};

export const filterNavConfigByPermissions = (
  navConfig: NavSectionProps['navConfig'],
  availablePermissions: string[] = [],
  isSuperAdmin = false
): NavSectionProps['navConfig'] =>
  navConfig
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) =>
          filterNavItem(item, availablePermissions, isSuperAdmin)
        )
        .filter(Boolean) as NavListProps[],
    }))
    .filter((group) => group.items.length);

export const getFirstNavItemPath = (
  navItems: NavListProps[]
): string | null => {
  for (const item of navItems) {
    if (item.children?.length) {
      const childPath = getFirstNavItemPath(item.children);
      if (childPath) {
        return childPath;
      }
    }

    if (item.path) {
      return item.path;
    }
  }

  return null;
};

export const getDefaultNavPath = (
  navConfig: NavSectionProps['navConfig'],
  availablePermissions: string[] = [],
  isSuperAdmin = false
): string | null => {
  const filteredNavConfig = filterNavConfigByPermissions(
    navConfig,
    availablePermissions,
    isSuperAdmin
  );

  for (const group of filteredNavConfig) {
    const path = getFirstNavItemPath(group.items);
    if (path) {
      return path;
    }
  }

  return null;
};
